import AddNotebookButton from "pages/notebooks/add-notebook-button";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import React, { useRef, useState } from "react";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import useToggleNotebookSidebar from "pages/notebooks/use-toggle-notebook-sidebar";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { t } from "utilities/localization-utils";
import { useHistory } from "react-router";
import useNotebookSidebar from "./use-notebook-sidebar";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import { ButtonSizes } from "atoms/constants/button-sizes";
import useToggleNotebookReferencePanel from "../notebook-reference-panel/use-toggle-notebook-reference-panel";
import Loader from "atoms/loaders/loader";
import { LoaderStyles } from "atoms/constants/loader-styles";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { NotebookConstants } from "constants/notebook-constants";
import NotebookRecord from "models/view-models/notebook-record";
import NotebookListSection from "./notebook-list-section";

const BASE_CLASS_NAME = "c-notebook-sidebar";

const NotebookSidebar = () => {
    const userId = useGlobalState().globalState.currentIdentity?.userId();
    const containerRef = useRef<HTMLDivElement>(null);
    const { showSidebar, toggleDisplay: toggleNotebookSidebar } =
        useToggleNotebookSidebar();
    const { showPanel, toggleDisplay: toggleReferencePanel } =
        useToggleNotebookReferencePanel();
    const { isMobile, isLaptop } = useNotebookBreakpoints();
    const { notebookRoute, getNotebookClassModifier } = useNotebookSidebar();
    const history = useHistory();
    const [openNotebookIds, setOpenNotebookIds] = useState<number[]>([]);
    const [sharedSectionOpen, setSharedSectionOpen] = useState(false);
    const [ownedSectionOpen, setOwnedSectionOpen] = useState(true);
    const { resultObject: notebooks, loading } = useNotebooksAtom();

    const handleToggleNotebookSidebarClick = () => {
        if (showPanel && isLaptop) {
            toggleReferencePanel();
        }
        toggleNotebookSidebar();
    };
    const handleNotebookTitleClick = (
        id: number,
        isOpen: boolean,
        shouldExpand: boolean
    ) => {
        if (shouldExpand) {
            handleNotebookExpand(id, isOpen);
        }
        navigateToNotebook(id);
    };

    const handleNotebookExpand = (id: number, isOpen: boolean) => {
        if (!isOpen) {
            setOpenNotebookIds((prev) => [...prev, id]);
        }
    };

    const navigateToNotebook = (notebookId: number) => {
        history.push(notebookRoute(notebookId)!);
        if (isMobile) {
            toggleNotebookSidebar();
        }
    };

    const handleNotebookClose = (id: number) => {
        setOpenNotebookIds((prev) => prev.filter((x) => x !== id));
    };

    const toggleSidebarButton = showSidebar ? "-open" : "-closed";
    const toggleSidebar = showSidebar ? "-show" : "-collapse";

    const isSharedNotebook = (notebook: NotebookRecord) => {
        return notebook.ownerId !== Number(userId);
    };
    const isSharedPagesNotebook = (notebook: NotebookRecord) => {
        return notebook.id === NotebookConstants.SharedPageNotebookId;
    };

    const sharedNotebooks = notebooks.filter(
        (notebook) =>
            isSharedPagesNotebook(notebook) || isSharedNotebook(notebook)
    );
    const ownedNotebooks = notebooks.filter(
        (notebook) =>
            !isSharedPagesNotebook(notebook) && !isSharedNotebook(notebook)
    );

    return (
        <>
            {!isMobile && (
                <Button
                    dataTestId={"notebook-sidebar--toggle-button"}
                    cssClassName={`${BASE_CLASS_NAME}__displayButton ${toggleSidebarButton}`}
                    onClick={handleToggleNotebookSidebarClick}
                    style={ButtonStyles.Tertiary}
                    ariaLabel={t("notebook-sidebar-toggle-button")}>
                    <Icon
                        type={
                            showSidebar ? Icons.ChevronLeft : Icons.ChevronRight
                        }
                    />
                </Button>
            )}
            {isMobile && (
                <div
                    className={`${BASE_CLASS_NAME}__mobileOverlay ${toggleSidebar}`}
                />
            )}
            <div className={`${BASE_CLASS_NAME} ${toggleSidebar}`}>
                <div className={`${BASE_CLASS_NAME}__header ${toggleSidebar}`}>
                    <div className={`${BASE_CLASS_NAME}__header__heading`}>
                        <Icon type={Icons.Notebook} size={IconSizes.Medium} />
                        {t("notebooks")}
                    </div>
                    {isMobile && (
                        <Button
                            dataTestId={"notebook-sidebar--toggle-button"}
                            accessibleText={t("close")}
                            cssClassName={"-modal-close -icon"}
                            onClick={handleToggleNotebookSidebarClick}
                            size={ButtonSizes.Medium}
                            style={ButtonStyles.TertiaryAlt}>
                            <Icon type={Icons.Close} size={IconSizes.Large} />
                        </Button>
                    )}
                </div>

                <div
                    className={`${BASE_CLASS_NAME}__itemContainer`}
                    ref={containerRef}>
                    {loading && <Loader type={LoaderStyles.LinkGlyphGray} />}

                    <NotebookListSection
                        buttonAriaText={t("yourNotebooks")}
                        collapse={!ownedSectionOpen}
                        getNotebookClassModifier={getNotebookClassModifier}
                        isSharedPagesNotebook={isSharedPagesNotebook}
                        notebooks={ownedNotebooks}
                        onNotebookClose={handleNotebookClose}
                        onNotebookExpand={handleNotebookExpand}
                        onNotebookTitleClick={handleNotebookTitleClick}
                        onSectionClose={() => setOwnedSectionOpen(false)}
                        onSectionOpen={() => setOwnedSectionOpen(true)}
                        openNotebookIds={openNotebookIds}
                        panelTop={
                            <div
                                className={`${BASE_CLASS_NAME}__sectionHeader`}>
                                <span>{t("yourNotebooks")}</span>
                            </div>
                        }
                    />

                    {sharedNotebooks.length > 0 && (
                        <NotebookListSection
                            buttonAriaText={t("sharedWithYou")}
                            collapse={!sharedSectionOpen}
                            getNotebookClassModifier={getNotebookClassModifier}
                            isSharedPagesNotebook={isSharedPagesNotebook}
                            isSharedSection
                            notebooks={sharedNotebooks}
                            onNotebookClose={handleNotebookClose}
                            onNotebookExpand={handleNotebookExpand}
                            onNotebookTitleClick={handleNotebookTitleClick}
                            onSectionClose={() => setSharedSectionOpen(false)}
                            onSectionOpen={() => setSharedSectionOpen(true)}
                            openNotebookIds={openNotebookIds}
                            panelTop={
                                <div
                                    className={`${BASE_CLASS_NAME}__sectionHeader`}>
                                    <span>{t("sharedWithYou")}</span>
                                    <Icon
                                        type={Icons.Team}
                                        size={IconSizes.Medium}
                                    />
                                </div>
                            }
                        />
                    )}

                    <div className={`${BASE_CLASS_NAME}__addNotebook`}>
                        <AddNotebookButton containerRef={containerRef} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotebookSidebar;
